import { Server } from 'socket.io';
import { AuthSocket } from '../socket.bootstrap';
import { SocketEvents } from '@ichat/shared-types';
import { prisma } from '../../shared/prisma';
import crypto from 'crypto';

interface ActiveCallRecord {
  id: string;
  callerId: string;
  receiverId: string;
  callType: string;
  roomName: string;
  startedAt: Date;
  connectedAt?: Date;
}

// In-memory registry to track active calls between users
const activeCallRegistry = new Map<string, ActiveCallRecord>();

const getCallKey = (u1: string, u2: string) => {
  return [u1, u2].sort().join(':::');
};

export function webrtcHandler(io: Server, socket: AuthSocket) {
  
  const handleOffer = async (data: { toUserId: string, offer: any, callerInfo?: any, callType?: string }) => {
    console.log(`📡 [WebRTC] CALL_OFFER from ${socket.userId} to ${data.toUserId}`);
    
    let displayName = socket.displayName || data.callerInfo?.displayName;
    let username = socket.username || data.callerInfo?.username;

    if (!displayName || displayName === 'User') {
      try {
        const user = await prisma.user.findUnique({
          where: { id: socket.userId },
          select: { displayName: true, username: true }
        });
        if (user) {
          displayName = user.displayName || user.username;
          username = user.username;
        }
      } catch (err) {
        console.warn('Error fetching caller details for WebRTC offer:', err);
      }
    }

    let roomName = '';
    let callType = 'audio';

    if (data.offer && typeof data.offer === 'object') {
      roomName = data.offer.roomName || '';
      callType = data.offer.callType || data.callType || 'audio';
    } else if (typeof data.offer === 'string') {
      roomName = data.offer;
      callType = (data as any).callType || 'audio';
    } else if (data.callType) {
      callType = data.callType;
    }

    if (roomName.includes('_audio_') || roomName.startsWith('room_audio_')) {
      callType = 'audio';
    } else if (roomName.includes('_video_') || roomName.startsWith('room_video_')) {
      callType = 'video';
    } else if (roomName.includes('_screen_') || roomName.startsWith('room_screen_')) {
      callType = 'screen';
    }

    const payload = {
      fromUserId: socket.userId,
      offer: data.offer,
      callType: callType,
      roomName: roomName,
      callerInfo: {
        id: socket.userId,
        displayName: displayName || username || 'Colleague',
        username: username || 'user'
      }
    };

    // Guard: Ignore self-calls or invalid target users
    if (!data.toUserId || socket.userId === data.toUserId) {
      console.warn(`[WebRTC] Ignoring invalid self-call attempt: caller ${socket.userId} -> target ${data.toUserId}`);
      return;
    }

    // Deduplication check: if a call between these users was initiated within the last 8 seconds or has the same roomName,
    // forward the signaling without inserting duplicate DB records
    const callKey = getCallKey(socket.userId, data.toUserId);
    const existing = activeCallRegistry.get(callKey);
    const now = Date.now();

    if (existing && (now - existing.startedAt.getTime() < 8000 || (roomName && existing.roomName === roomName))) {
      io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:offer', payload);
      return;
    }

    // Create Call Log in Database
    const callLogId = crypto.randomUUID();

    activeCallRegistry.set(callKey, {
      id: callLogId,
      callerId: socket.userId,
      receiverId: data.toUserId,
      callType: callType,
      roomName: roomName,
      startedAt: new Date(),
    });

    try {
      if ((prisma as any).callLog?.create) {
        await (prisma as any).callLog.create({
          data: {
            id: callLogId,
            callerId: socket.userId,
            receiverId: data.toUserId,
            callType: callType,
            status: 'ringing',
            startedAt: new Date(),
            roomName: roomName,
            duration: 0,
          }
        });
      } else {
        await prisma.$executeRawUnsafe(
          `INSERT INTO "call_logs" ("id", "callerId", "receiverId", "callType", "status", "startedAt", "roomName", "duration") VALUES ($1, $2, $3, $4, $5, NOW(), $6, 0)`,
          callLogId, socket.userId, data.toUserId, callType, 'ringing', roomName
        );
      }
      console.log(`📝 [CallLog] Created call log entry: ${callLogId} (${callType}) from ${socket.userId} to ${data.toUserId}`);
      io.emit('ADMIN_CALL_LOG_PING', { action: 'created', callLogId });
      io.to(`user:${socket.userId}`).to(`user:${data.toUserId}`).emit('USER_CALL_LOG_PING', { action: 'created', callLogId });
    } catch (err) {
      console.error('❌ [CallLog] Failed to initialize CallLog in database:', err);
    }

    // Send the offer to the target user's personal room
    io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:offer', payload);
  };

  const handleAnswer = (data: { toUserId: string, answer: any }) => {
    io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:answer', {
      fromUserId: socket.userId,
      answer: data.answer
    });
  };

  const handleCandidate = (data: { toUserId: string, candidate: any }) => {
    io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:ice_candidate', {
      fromUserId: socket.userId,
      candidate: data.candidate
    });
  };

  const handleEnd = async (data: any) => {
    const toUserId = data?.toUserId || (data && typeof data === 'object' ? data.targetUserId : undefined);
    
    // Find active call if targetUserId was not directly supplied
    const callKey = toUserId ? getCallKey(socket.userId, toUserId) : null;
    let active = callKey ? activeCallRegistry.get(callKey) : null;
    let foundKey = callKey;
    
    if (!active) {
      for (const [key, call] of activeCallRegistry.entries()) {
        if (call.callerId === socket.userId || call.receiverId === socket.userId) {
          active = call;
          foundKey = key;
          break;
        }
      }
    }

    const peerId = toUserId || (active ? (active.callerId === socket.userId ? active.receiverId : active.callerId) : undefined);

    console.log(`🔥 [WebRTC Handler] Ending call. Socket: ${socket.userId}, Peer: ${peerId}, Active: ${active?.id || 'none'}`);

    if (peerId) {
      io.to(`user:${peerId}`).to(peerId).emit('call:end', { fromUserId: socket.userId });
      io.to(`user:${peerId}`).to(peerId).emit('CALL_END', { fromUserId: socket.userId });
    }

    if (active?.roomName) {
      io.to(active.roomName).emit('call:end', { fromUserId: socket.userId });
      io.to(active.roomName).emit('CALL_END', { fromUserId: socket.userId });
    }

    if (active) {
      const endedAt = new Date();
      let duration = 0;
      let status = 'missed';

      const startTime = active.connectedAt || active.startedAt;
      if (startTime) {
        duration = Math.max(1, Math.round((endedAt.getTime() - startTime.getTime()) / 1000));
        status = active.connectedAt ? 'completed' : (active.callerId === socket.userId ? 'cancelled' : 'missed');
      }

      try {
        if ((prisma as any).callLog?.update) {
          await (prisma as any).callLog.update({
            where: { id: active.id },
            data: {
              status,
              endedAt,
              duration,
            }
          });
        } else {
          await prisma.$executeRawUnsafe(
            `UPDATE "call_logs" SET "status" = $1, "endedAt" = $2, "duration" = $3 WHERE "id" = $4`,
            status, endedAt, duration, active.id
          );
        }
        console.log(`📝 [CallLog] Ended call log ${active.id} with status: ${status}, duration: ${duration}s`);
        io.emit('ADMIN_CALL_LOG_PING', { action: 'ended', callLogId: active.id });
        io.to(`user:${active.callerId}`).to(`user:${active.receiverId}`).emit('USER_CALL_LOG_PING', { action: 'ended', callLogId: active.id });
      } catch (err) {
        console.error('❌ [CallLog] Failed to update CallLog on call:end:', err);
      }

      if (foundKey) {
        activeCallRegistry.delete(foundKey);
      }
    } else {
      // Fallback: update latest unended call between these users
      try {
        const endedAt = new Date();
        if ((prisma as any).callLog?.findFirst) {
          const whereClause: any = peerId ? {
            OR: [
              { callerId: socket.userId, receiverId: peerId },
              { callerId: peerId, receiverId: socket.userId }
            ],
            endedAt: null
          } : {
            OR: [
              { callerId: socket.userId },
              { receiverId: socket.userId }
            ],
            endedAt: null
          };

          const latest = await (prisma as any).callLog.findFirst({
            where: whereClause,
            orderBy: { startedAt: 'desc' }
          });
          if (latest) {
            const duration = Math.max(1, Math.round((endedAt.getTime() - new Date(latest.startedAt).getTime()) / 1000));
            await (prisma as any).callLog.update({
              where: { id: latest.id },
              data: {
                status: latest.connectedAt ? 'completed' : 'cancelled',
                endedAt,
                duration
              }
            });
            io.emit('ADMIN_CALL_LOG_PING', { action: 'ended', callLogId: latest.id });
            io.to(`user:${latest.callerId}`).to(`user:${latest.receiverId}`).emit('USER_CALL_LOG_PING', { action: 'ended', callLogId: latest.id });
          }
        }
      } catch (e) {}
    }
  };

  const handleRinging = (data: { toUserId: string }) => {
    io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:ringing', {
      fromUserId: socket.userId
    });
  };

  const handleRejected = async (data: { toUserId: string }) => {
    io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:rejected', {
      fromUserId: socket.userId
    });

    const callKey = getCallKey(socket.userId, data.toUserId);
    const active = activeCallRegistry.get(callKey);

    if (active) {
      try {
        if ((prisma as any).callLog?.update) {
          await (prisma as any).callLog.update({
            where: { id: active.id },
            data: {
              status: 'rejected',
              endedAt: new Date(),
              duration: 0,
            }
          });
        } else {
          await prisma.$executeRawUnsafe(
            `UPDATE "call_logs" SET "status" = 'rejected', "endedAt" = NOW(), "duration" = 0 WHERE "id" = $1`,
            active.id
          );
        }
        io.emit('ADMIN_CALL_LOG_PING', { action: 'rejected', callLogId: active.id });
        io.to(`user:${active.callerId}`).to(`user:${active.receiverId}`).emit('USER_CALL_LOG_PING', { action: 'rejected', callLogId: active.id });
      } catch (err) {
        console.error('❌ [CallLog] Failed to update CallLog on call:rejected:', err);
      }
      activeCallRegistry.delete(callKey);
    }
  };

  const handleAccepted = async (data: { toUserId: string }) => {
    io.to(`user:${data.toUserId}`).to(data.toUserId).emit('call:accepted', {
      fromUserId: socket.userId
    });

    const callKey = getCallKey(socket.userId, data.toUserId);
    const active = activeCallRegistry.get(callKey);

    if (active) {
      active.connectedAt = new Date();
      try {
        if ((prisma as any).callLog?.update) {
          await (prisma as any).callLog.update({
            where: { id: active.id },
            data: {
              status: 'completed',
              connectedAt: active.connectedAt,
            }
          });
        } else {
          await prisma.$executeRawUnsafe(
            `UPDATE "call_logs" SET "status" = 'completed', "connectedAt" = NOW() WHERE "id" = $1`,
            active.id
          );
        }
        console.log(`📝 [CallLog] Call accepted & marked completed: ${active.id}`);
        io.emit('ADMIN_CALL_LOG_PING', { action: 'accepted', callLogId: active.id });
        io.to(`user:${active.callerId}`).to(`user:${active.receiverId}`).emit('USER_CALL_LOG_PING', { action: 'accepted', callLogId: active.id });
      } catch (err) {
        console.error('❌ [CallLog] Failed to update CallLog on call:accepted:', err);
      }
    }
  };

  socket.on('call:offer', handleOffer);
  socket.on('CALL_OFFER', handleOffer);

  socket.on('call:answer', handleAnswer);
  socket.on('CALL_ANSWER', handleAnswer);

  socket.on('call:ice_candidate', handleCandidate);
  socket.on('CALL_ICE_CANDIDATE', handleCandidate);

  socket.on('call:end', handleEnd);
  socket.on('CALL_END', handleEnd);

  socket.on('call:ringing', handleRinging);
  socket.on('CALL_RINGING', handleRinging);

  socket.on('call:rejected', handleRejected);
  socket.on('CALL_REJECTED', handleRejected);

  socket.on('call:accepted', handleAccepted);
  socket.on('CALL_ACCEPTED', handleAccepted);
}
